import { randomBytes, createHash } from 'node:crypto'
import type { Firestore } from 'firebase-admin/firestore'
import type { RedisClient } from '../../core/redis/client.js'
import type {
  CreateShareDTO,
  ShareLink,
  Comment,
  CreateCommentDTO,
  PublicPlanResponse,
  PublicPlanDetails
} from '@my-roadtrip/types'
import { SharingRepository } from './sharing.repository.js'
import { createLogger } from '../../common/utils/logger.js'

const logger = createLogger('SharingService')

/**
 * SharingService - Manages plan sharing, collaborators, and share links.
 * 
 * ⚠️ TECHNICAL DEBT: Dual permission system
 * 
 * Currently, permissions are stored in TWO places:
 * 1. `sharedWith[]` + `sharedPermissions{}` - Legacy flat arrays (DEPRECATED)
 * 2. `collaborators[]` - New rich collaborator objects (PREFERRED)
 * 
 * Both are kept in sync for backward compatibility but this is error-prone.
 * 
 * MIGRATION PLAN:
 * 1. All new code should read from `collaborators[]` only
 * 2. Write operations must update BOTH until migration is complete
 * 3. Run migration script to consolidate data
 * 4. Remove `sharedWith`/`sharedPermissions` after verification
 * 
 * Source of truth: `collaborators[]` with PlanCollaborator interface
 */
export class SharingService {
  private readonly repository: SharingRepository

  constructor(
    private readonly firestore: Firestore,
    private readonly redis: RedisClient
  ) {
    this.repository = new SharingRepository(firestore)
  }

  async createShare(planId: string, userId: string, data: CreateShareDTO): Promise<ShareLink> {
    logger.info('Creating share link', { planId, userId, privacy: data.privacy })
    const hashedPassword = data.password ? this.hashPassword(data.password) : undefined

    // Upsert: reuse the latest share link by updating its parameters instead of creating a new token
    const existing = await this.repository.getLatestShare(planId)
    if (existing) {
      logger.info('Updating existing share link', { planId, shareId: existing.id })
      const updates: Partial<ShareLink> = {
        privacy: data.privacy,
        password: hashedPassword,
        expiresAt: data.expiresAt ? new Date(data.expiresAt) : undefined,
        allowComments: data.allowComments ?? existing.allowComments ?? false,
        updatedAt: new Date()
      }

      const updatedShare = await this.repository.updateShare(existing.id, updates)
      await this.redis.setex(`share:${existing.token}`, 600, JSON.stringify(updatedShare))
      return this.sanitizeShare(updatedShare)
    }

    const token = this.generateToken()

    const share: Omit<ShareLink, 'id'> = {
      planId,
      token,
      privacy: data.privacy,
      password: hashedPassword,
      expiresAt: data.expiresAt ? new Date(data.expiresAt) : undefined,
      viewCount: 0,
      createdBy: userId,
      createdAt: new Date(),
      updatedAt: new Date(),
      allowComments: data.allowComments ?? false
    }

    const shareId = await this.repository.createShare(share)
    logger.info('Share link created', { planId, userId, shareId, privacy: data.privacy })

    return this.sanitizeShare({ id: shareId, ...share })
  }

  async listShares(planId: string, userId: string): Promise<ShareLink[]> {
    logger.debug('Listing shares', { planId, userId })
    const shares = await this.repository.getSharesByPlan(planId)
    const userShares = shares
      .filter(share => share.createdBy === userId)
      .map(share => this.sanitizeShare(share))
    logger.info('Shares listed', { planId, userId, count: userShares.length })
    return userShares
  }

  async areCommentsEnabled(planId: string): Promise<boolean> {
    const shares = await this.repository.getSharesByPlan(planId)
    const now = Date.now()
    return shares.some(share => {
      if (!share.allowComments) return false
      if (share.expiresAt && share.expiresAt.getTime() < now) return false
      return true
    })
  }

  async accessShare(token: string, password?: string): Promise<ShareLink> {
    logger.debug('Accessing share', { token: token.substring(0, 10) })
    const share = await this.getShareByToken(token)
    if (!share) {
      logger.warn('Share not found', { token: token.substring(0, 10) })
      const error: any = new Error('SHARE_NOT_FOUND')
      error.statusCode = 404
      throw error
    }

    if (share.expiresAt && share.expiresAt.getTime() < Date.now()) {
      logger.warn('Share expired', { shareId: share.id })
      const error: any = new Error('SHARE_EXPIRED')
      error.statusCode = 410
      throw error
    }

    if (share.password) {
      if (!password) {
        logger.debug('Password required for share access', { shareId: share.id })
        const error: any = new Error('PASSWORD_REQUIRED')
        error.statusCode = 401
        throw error
      }
      const valid = this.verifyPassword(password, share.password)
      if (!valid) {
        logger.warn('Invalid password for share', { shareId: share.id })
        const error: any = new Error('INVALID_PASSWORD')
        error.statusCode = 401
        throw error
      }
    }

    await this.incrementViewCount(share.id)
    const nextViewCount = (share.viewCount ?? 0) + 1
    logger.info('Share accessed', { shareId: share.id, viewCount: nextViewCount })
    return this.sanitizeShare({ ...share, viewCount: nextViewCount })
  }

  async getShareByToken(token: string): Promise<ShareLink | null> {
    logger.debug('Fetching share by token', { token: token.substring(0, 10) })
    // Check cache
    const cacheKey = `share:${token}`
    const cached = await this.redis.get(cacheKey)
    if (cached) {
      try {
        logger.debug('Share token cache hit')
        return JSON.parse(cached)
      } catch (error) {
        logger.warn('Failed to parse cached share', { error: (error as Error).message })
        await this.redis.del(cacheKey)
        // Continue to fetch from DB
      }
    }

    logger.debug('Share token cache miss, querying DB')
    // Get from DB
    const share = await this.repository.findByToken(token)
    if (!share) {
      logger.warn('Share token not found in DB', { token: token.substring(0, 10) })
      return null
    }

    // Cache for 10 min
    await this.redis.setex(cacheKey, 600, JSON.stringify(share))

    return share
  }

  async getShareById(shareId: string): Promise<ShareLink | null> {
    logger.debug('Fetching share by ID', { shareId })
    return this.repository.getShareById(shareId)
  }

  async deleteShare(shareId: string): Promise<void> {
    logger.info('Deleting share', { shareId })
    const share = await this.repository.getShareById(shareId)
    if (share) {
      await this.redis.del(`share:${share.token}`)
    }
    await this.repository.deleteShare(shareId)
    logger.info('Share deleted', { shareId })
  }

  async incrementViewCount(shareId: string): Promise<void> {
    await this.repository.incrementViewCount(shareId)
  }

  verifyPassword(plain: string, hashed: string): boolean {
    return this.hashPassword(plain) === hashed
  }

  async getComments(planId: string): Promise<Comment[]> {
    logger.debug('Fetching comments', { planId })
    return this.repository.getComments(planId)
  }

  async createComment(planId: string, userId: string, data: CreateCommentDTO): Promise<Comment> {
    logger.info('Creating comment', { planId, userId })
    // Get user display name
    let userName = 'Anonymous'
    try {
      const userDoc = await this.firestore.collection('users').doc(userId).get()
      if (userDoc.exists) {
        const userData = userDoc.data()
        userName = userData?.displayName || userData?.email?.split('@')[0] || 'Anonymous'
      }
    } catch (err) {
      logger.warn('Failed to get user profile for comment', { userId, error: err })
    }

    const now = new Date()
    const comment: Omit<Comment, 'id'> = {
      planId,
      userId,
      userName,
      content: data.content,
      parentId: data.parentId,
      replies: [],
      createdAt: now,
      updatedAt: now
    }

    const commentId = await this.repository.createComment(comment)

    return { id: commentId, ...comment }
  }

  async getCommentById(commentId: string): Promise<Comment | null> {
    return this.repository.getCommentById(commentId)
  }

  async deleteComment(commentId: string): Promise<void> {
    await this.repository.deleteComment(commentId)
  }

  async getPublicPlan(token: string, password?: string): Promise<PublicPlanResponse> {
    const share = await this.getShareByToken(token)
    if (!share) {
      const error: any = new Error('SHARE_NOT_FOUND')
      error.statusCode = 404
      throw error
    }

    if (share.expiresAt && share.expiresAt.getTime() < Date.now()) {
      const error: any = new Error('SHARE_EXPIRED')
      error.statusCode = 410
      throw error
    }

    if (share.password) {
      if (!password) {
        return { requiresPassword: true, share: this.sanitizeShare(share) }
      }
      const valid = this.verifyPassword(password, share.password)
      if (!valid) {
        const error: any = new Error('INVALID_PASSWORD')
        error.statusCode = 401
        throw error
      }
    }

    await this.incrementViewCount(share.id)

    const plan = await this.repository.getPlanById(share.planId)
    if (!plan) {
      const error: any = new Error('PLAN_NOT_FOUND')
      error.statusCode = 404
      throw error
    }

    const stages = await this.repository.getPlanStages(share.planId)
    const ownerProfile = await this.repository.getUserProfile(plan.userId || share.createdBy)

    const response: PublicPlanDetails = {
      plan,
      stages,
      share: this.sanitizeShare({ ...share, viewCount: share.viewCount + 1 }),
      ownerName: ownerProfile?.displayName ?? null
    }

    return response
  }

  private generateToken(): string {
    return randomBytes(16).toString('hex')
  }

  private hashPassword(password: string): string {
    return createHash('sha256').update(password).digest('hex')
  }

  private sanitizeShare(share: ShareLink): ShareLink {
    const { password, ...rest } = share
    return {
      ...rest,
      hasPassword: Boolean(password)
    } as ShareLink
  }

  /**
   * Join a shared plan - adds the user as a collaborator on the ORIGINAL plan
   * (not a copy)
   */
  async joinPlan(token: string, userId: string, password?: string): Promise<{ planId: string; permission: 'view' | 'edit' }> {
    logger.info('Join plan requested', { token: token.substring(0, 10), userId })

    // Validate share token
    const share = await this.getShareByToken(token)
    if (!share) {
      logger.warn('Join plan failed: share not found', { token: token.substring(0, 10) })
      const error: any = new Error('SHARE_NOT_FOUND')
      error.statusCode = 404
      throw error
    }

    // Check expiration
    if (share.expiresAt && share.expiresAt.getTime() < Date.now()) {
      logger.warn('Join plan failed: share expired', { shareId: share.id })
      const error: any = new Error('SHARE_EXPIRED')
      error.statusCode = 410
      throw error
    }

    // Check password
    if (share.password) {
      if (!password) {
        logger.debug('Password required for joining', { shareId: share.id })
        const error: any = new Error('PASSWORD_REQUIRED')
        error.statusCode = 401
        throw error
      }
      if (!this.verifyPassword(password, share.password)) {
        logger.warn('Join plan failed: invalid password', { shareId: share.id })
        const error: any = new Error('INVALID_PASSWORD')
        error.statusCode = 401
        throw error
      }
    }

    // Get the plan
    const plan = await this.repository.getPlanById(share.planId)
    if (!plan) {
      logger.warn('Join plan failed: plan not found', { planId: share.planId })
      const error: any = new Error('PLAN_NOT_FOUND')
      error.statusCode = 404
      throw error
    }

    // Don't allow owner to "join" their own plan
    if (plan.userId === userId) {
      logger.info('User already owns this plan', { planId: share.planId, userId })
      return { planId: share.planId, permission: 'edit' }
    }

    // Check if already a collaborator
    const existingCollaborator = plan.collaborators?.find(c => c.userId === userId)
    if (existingCollaborator) {
      logger.info('User already a collaborator', { planId: share.planId, userId, role: existingCollaborator.role })
      const permission = existingCollaborator.role === 'viewer' ? 'view' : 'edit'
      return { planId: share.planId, permission }
    }

    // Determine permission based on share privacy
    // Public shares give view access, private shares (with password) give edit access
    const permission: 'view' | 'edit' = share.privacy === 'private' ? 'edit' : 'view'
    const role = permission === 'edit' ? 'editor' : 'viewer'

    // Add user to sharedWith and collaborators
    const sharedWith = Array.from(new Set([...(plan.sharedWith || []), userId]))
    const sharedPermissions: Record<string, 'view' | 'edit'> = plan.sharedPermissions 
      ? { ...plan.sharedPermissions } 
      : {}
    sharedPermissions[userId] = permission

    const collaborators = [...(plan.collaborators || [])]
    collaborators.push({
      userId,
      role
    })

    // Update the plan
    await this.repository.updatePlan(share.planId, {
      sharedWith,
      sharedPermissions,
      collaborators
    })

    logger.info('User joined plan successfully', { 
      planId: share.planId, 
      userId, 
      permission,
      role 
    })

    return { planId: share.planId, permission }
  }

  /**
   * Update a collaborator's role
   */
  async updateCollaboratorRole(
    planId: string, 
    requestingUserId: string, 
    targetUserId: string, 
    newRole: 'admin' | 'editor' | 'viewer'
  ): Promise<void> {
    logger.info('Update collaborator role requested', { planId, requestingUserId, targetUserId, newRole })

    const plan = await this.repository.getPlanById(planId)
    if (!plan) {
      const error: any = new Error('PLAN_NOT_FOUND')
      error.statusCode = 404
      throw error
    }

    // Check if requester has permission (owner or admin)
    const isOwner = plan.userId === requestingUserId
    const requesterCollab = plan.collaborators?.find(c => c.userId === requestingUserId)
    const isAdmin = requesterCollab?.role === 'admin'

    if (!isOwner && !isAdmin) {
      logger.warn('Update role denied: insufficient permissions', { planId, requestingUserId })
      const error: any = new Error('FORBIDDEN')
      error.statusCode = 403
      throw error
    }

    // Can't change owner's role
    if (plan.userId === targetUserId) {
      logger.warn('Cannot change owner role', { planId, targetUserId })
      const error: any = new Error('CANNOT_CHANGE_OWNER')
      error.statusCode = 400
      throw error
    }

    // Admin can't change admin/owner roles
    if (!isOwner) {
      const targetCollab = plan.collaborators?.find(c => c.userId === targetUserId)
      if (targetCollab?.role === 'admin' || targetCollab?.role === 'owner') {
        logger.warn('Admin cannot change admin/owner roles', { planId, targetUserId, targetRole: targetCollab.role })
        const error: any = new Error('FORBIDDEN')
        error.statusCode = 403
        throw error
      }
      // Admin can't promote to admin
      if (newRole === 'admin') {
        logger.warn('Admin cannot promote to admin', { planId, targetUserId })
        const error: any = new Error('FORBIDDEN')
        error.statusCode = 403
        throw error
      }
    }

    // Update collaborator role
    const collaborators = (plan.collaborators || []).map(c => 
      c.userId === targetUserId ? { ...c, role: newRole } : c
    )

    // Also update sharedPermissions for legacy compatibility
    const sharedPermissions = { ...(plan.sharedPermissions || {}) }
    sharedPermissions[targetUserId] = newRole === 'viewer' ? 'view' : 'edit'

    await this.repository.updatePlan(planId, {
      collaborators,
      sharedPermissions
    })

    logger.info('Collaborator role updated', { planId, targetUserId, newRole })
  }

  /**
   * Remove a collaborator from a plan
   */
  async removeCollaborator(
    planId: string, 
    requestingUserId: string, 
    targetUserId: string
  ): Promise<void> {
    logger.info('Remove collaborator requested', { planId, requestingUserId, targetUserId })

    const plan = await this.repository.getPlanById(planId)
    if (!plan) {
      const error: any = new Error('PLAN_NOT_FOUND')
      error.statusCode = 404
      throw error
    }

    // Check if requester has permission (owner or admin)
    const isOwner = plan.userId === requestingUserId
    const requesterCollab = plan.collaborators?.find(c => c.userId === requestingUserId)
    const isAdmin = requesterCollab?.role === 'admin'
    const isSelf = requestingUserId === targetUserId

    // Allow self-removal (leaving the plan)
    if (!isOwner && !isAdmin && !isSelf) {
      logger.warn('Remove collaborator denied: insufficient permissions', { planId, requestingUserId })
      const error: any = new Error('FORBIDDEN')
      error.statusCode = 403
      throw error
    }

    // Can't remove owner
    if (plan.userId === targetUserId) {
      logger.warn('Cannot remove owner', { planId, targetUserId })
      const error: any = new Error('CANNOT_REMOVE_OWNER')
      error.statusCode = 400
      throw error
    }

    // Admin can't remove other admins
    if (!isOwner && !isSelf) {
      const targetCollab = plan.collaborators?.find(c => c.userId === targetUserId)
      if (targetCollab?.role === 'admin' || targetCollab?.role === 'owner') {
        logger.warn('Admin cannot remove admin/owner', { planId, targetUserId })
        const error: any = new Error('FORBIDDEN')
        error.statusCode = 403
        throw error
      }
    }

    // Remove from all arrays
    const collaborators = (plan.collaborators || []).filter(c => c.userId !== targetUserId)
    const sharedWith = (plan.sharedWith || []).filter(id => id !== targetUserId)
    const sharedPermissions = { ...(plan.sharedPermissions || {}) }
    delete sharedPermissions[targetUserId]

    await this.repository.updatePlan(planId, {
      collaborators,
      sharedWith,
      sharedPermissions
    })

    logger.info('Collaborator removed', { planId, targetUserId })
  }

  /**
   * Update a collaborator's custom permissions
   */
  async updateCollaboratorPermissions(
    planId: string,
    requestingUserId: string,
    targetUserId: string,
    permissions: Record<string, boolean>
  ): Promise<void> {
    logger.info('Update collaborator permissions requested', { planId, requestingUserId, targetUserId })

    const plan = await this.repository.getPlanById(planId)
    if (!plan) {
      const error: any = new Error('PLAN_NOT_FOUND')
      error.statusCode = 404
      throw error
    }

    // Only owner and admin can change permissions
    const isOwner = plan.userId === requestingUserId
    const requesterCollab = plan.collaborators?.find(c => c.userId === requestingUserId)
    const isAdmin = requesterCollab?.role === 'admin'

    if (!isOwner && !isAdmin) {
      logger.warn('Update permissions denied: insufficient permissions', { planId, requestingUserId })
      const error: any = new Error('FORBIDDEN')
      error.statusCode = 403
      throw error
    }

    // Can't change owner permissions
    if (plan.userId === targetUserId) {
      logger.warn('Cannot change owner permissions', { planId, targetUserId })
      const error: any = new Error('CANNOT_CHANGE_OWNER')
      error.statusCode = 400
      throw error
    }

    // Update collaborator permissions
    const collaborators = (plan.collaborators || []).map(c => {
      if (c.userId === targetUserId) {
        return {
          ...c,
          permissions: {
            ...(c.permissions || {}),
            ...permissions
          }
        }
      }
      return c
    })

    await this.repository.updatePlan(planId, { collaborators })

    logger.info('Collaborator permissions updated', { planId, targetUserId })
  }

  /**
   * Get collaborators for a plan with presence data
   */
  async getCollaborators(planId: string): Promise<Array<{
    userId: string
    role: string
    displayName?: string
    email?: string
    iconSet?: { activeIcon: string; accentColor?: string } | null
    status: 'active' | 'offline'
    lastSeen?: Date
    permissions?: Record<string, boolean>
  }>> {
    const plan = await this.repository.getPlanById(planId)
    if (!plan) {
      logger.warn('Plan not found for collaborators', { planId })
      return []
    }

    const collaborators = plan.collaborators || []
    
    logger.debug('Getting collaborators for plan', { 
      planId, 
      collaboratorCount: collaborators.length
    })
    
    // Enrich with user profiles
    const enriched = await Promise.all(
      collaborators.map(async (c) => {
        const profile = await this.repository.getUserProfile(c.userId)
        return {
          userId: c.userId,
          role: c.role,
          displayName: profile?.displayName || c.displayName,
          email: profile?.email || c.email,
          iconSet: profile?.iconSet || null,
          status: 'offline' as const, // Will be enriched with presence data
          lastSeen: c.lastSeen,
          permissions: c.permissions
        }
      })
    )

    return enriched
  }

  /**
   * Get owner profile by userId
   */
  async getOwnerProfile(userId: string): Promise<{
    displayName?: string | null
    email?: string | null
    iconSet?: { activeIcon: string; accentColor?: string } | null
  } | null> {
    return this.repository.getUserProfile(userId)
  }
}